import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';
import { Settings, User, Lock, DollarSign, Gamepad2, Gift, ArrowDownCircle, ArrowUpCircle, Zap, Save, Globe, Mail } from 'lucide-react';

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('support');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rules, setRules] = useState({
    deposit_rules: { min_amount: 10, max_amount: 10000, enabled: true, first_deposit_bonus_percent: 100, regular_bonus_percent: 20 },
    withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0, enabled: true, fee_percent: 0, min_balance_after: 0 },
    load_rules: { min_xp: 10, max_xp: 100000, enabled: true },
    redeem_rules: { min_provider_amount: 1, max_provider_amount: 10000, enabled: true },
    bonus_rules: { first_deposit_bonus_pct: 100, signup_bonus_xp: 50, referral_bonus_xp: 25, enabled: true },
    xp_conversion: { website_currency: 'USD', provider_currency: 'USD', load_xp_per_provider_unit: 1, redeem_xp_per_provider_unit: 1 },
    referral_rules: { player_referrer_bonus_sc: 25, player_referred_bonus_sc: 10, player_referrer_load_percent: 0, staff_referral_enabled: true, player_referral_enabled: true },
    support_channels: { facebook: '', whatsapp: '', telegram: '', messenger: '' },
  });

  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [mailStatus, setMailStatus] = useState(null);
  const [mailConfig, setMailConfig] = useState({
    smtp_host: 'smtppro.zoho.in',
    smtp_port: '587',
    smtp_user: 'verify@playemp.vip',
    smtp_pass: '',
    smtp_from: 'verify@playemp.vip',
    smtp_from_name: 'PlayEMP',
  });
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [mailSaving, setMailSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchMailStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/admin/settings').catch(() => ({ data: {} }));
      const s = res.data.settings || res.data || {};
      setRules(prev => ({
        ...prev,
        ...s,
        support_channels: { ...prev.support_channels, ...(s.support_channels || {}) },
      }));
    } catch {} finally { setLoading(false); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/settings', rules);
      toast.success('Platform rules saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const fetchMailStatus = async () => {
    try {
      const res = await api.get('/api/admin/smtp/status');
      const s = res.data || {};
      setMailStatus(s);
      setMailConfig(prev => ({
        ...prev,
        smtp_host: s.smtp_host || prev.smtp_host,
        smtp_port: String(s.smtp_port || prev.smtp_port),
        smtp_user: s.smtp_user || prev.smtp_user,
        smtp_from: s.smtp_from || prev.smtp_from,
        smtp_from_name: s.smtp_from_name || prev.smtp_from_name,
      }));
    } catch {}
  };

  const saveMailConfig = async () => {
    setMailSaving(true);
    try {
      await api.put('/api/admin/smtp/config', mailConfig);
      toast.success('Zoho mail saved for this tenant');
      setMailConfig(prev => ({ ...prev, smtp_pass: '' }));
      await fetchMailStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mail config');
    } finally {
      setMailSaving(false);
    }
  };

  const sendTestMail = async () => {
    if (!testEmail) return toast.error('Enter a test email');
    try {
      const res = await api.post('/api/admin/smtp/test', { email: testEmail });
      if (res.data?.success) toast.success(res.data?.message || 'Test email sent');
      else toast.error(res.data?.message || res.data?.error || 'Test failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error('Passwords do not match'); return; }
    try {
      await api.post('/api/auth/change-password', { old_password: passwordData.current_password, new_password: passwordData.new_password });
      toast.success('Password changed');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateRule = (section, key, value) => {
    setRules(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const tabs = [
    { id: 'support', label: 'Support Channels', icon: Globe },
    { id: 'mail', label: 'Zoho Mail', icon: Mail },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="settings-page">
      <h2 className="text-xl font-semibold">Platform Settings</h2>

      <div className="flex gap-1 bg-[#131822] rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap ${activeTab === t.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : (
        <>
          {activeTab === 'rules' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">General Load/Redeem Rules</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Load ($)" value={rules.load_rules.min_xp} onChange={v => updateRule('load_rules', 'min_xp', v)} />
                <RuleInput label="Max Load ($)" value={rules.load_rules.max_xp} onChange={v => updateRule('load_rules', 'max_xp', v)} />
                <RuleInput label="Min Redeem (Provider $)" value={rules.redeem_rules.min_provider_amount} onChange={v => updateRule('redeem_rules', 'min_provider_amount', v)} />
                <RuleInput label="Max Redeem (Provider $)" value={rules.redeem_rules.max_provider_amount} onChange={v => updateRule('redeem_rules', 'max_provider_amount', v)} />
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black" data-testid="save-rules-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Rules'}
              </Button>
            </div>
          )}

          {activeTab === 'deposit' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Deposit Rules</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Deposit ($)" value={rules.deposit_rules.min_amount} onChange={v => updateRule('deposit_rules', 'min_amount', v)} />
                <RuleInput label="Max Deposit ($)" value={rules.deposit_rules.max_amount} onChange={v => updateRule('deposit_rules', 'max_amount', v)} />
                <RuleInput label="First Deposit Bonus %" value={rules.deposit_rules.first_deposit_bonus_percent} onChange={v => updateRule('deposit_rules', 'first_deposit_bonus_percent', v)} />
                <RuleInput label="Regular Deposit Bonus %" value={rules.deposit_rules.regular_bonus_percent} onChange={v => updateRule('deposit_rules', 'regular_bonus_percent', v)} />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Deposits Enabled</label>
                <button onClick={() => updateRule('deposit_rules', 'enabled', !rules.deposit_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.deposit_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.deposit_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'withdrawal' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Withdrawal Rules</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Min Withdrawal ($)" value={rules.withdrawal_rules.min_amount} onChange={v => updateRule('withdrawal_rules', 'min_amount', v)} />
                <RuleInput label="Max Withdrawal ($)" value={rules.withdrawal_rules.max_amount} onChange={v => updateRule('withdrawal_rules', 'max_amount', v)} />
                <RuleInput label="Max Withdrawal Per Day ($)" value={rules.withdrawal_rules.max_total_per_day} onChange={v => updateRule('withdrawal_rules', 'max_total_per_day', v)} />
                <RuleInput label="Withdrawal Fee %" value={rules.withdrawal_rules.fee_percent} onChange={v => updateRule('withdrawal_rules', 'fee_percent', v)} />
                <RuleInput label="Min Balance After" value={rules.withdrawal_rules.min_balance_after} onChange={v => updateRule('withdrawal_rules', 'min_balance_after', v)} />
              </div>
              <p className="text-xs text-gray-500">Set Max Withdrawal Per Day to 0 for no daily cap.</p>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'credits' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Game Credit Ratio</h3>
              <p className="text-sm text-gray-400">Controls how main wallet dollars convert to game provider credits. Default is $1 wallet = $1 game credit.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="Load: Wallet $ per $1 Provider" value={rules.xp_conversion.load_xp_per_provider_unit} onChange={v => updateRule('xp_conversion', 'load_xp_per_provider_unit', v)} />
                <RuleInput label="Redeem: Wallet $ per $1 Provider" value={rules.xp_conversion.redeem_xp_per_provider_unit} onChange={v => updateRule('xp_conversion', 'redeem_xp_per_provider_unit', v)} />
              </div>
              <div className="bg-[#0B0E14] rounded-lg p-4">
                <p className="text-xs text-gray-500">With current settings:</p>
                <p className="text-sm text-[#D4AF37]">$100 wallet = ${(100 / (rules.xp_conversion.load_xp_per_provider_unit || 1)).toFixed(2)} game credits (load)</p>
                <p className="text-sm text-[#D4AF37]">$10 game credits = ${(10 * (rules.xp_conversion.redeem_xp_per_provider_unit || 1)).toFixed(2)} wallet (redeem)</p>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'bonus' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Bonus Rules</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <RuleInput label="First Deposit Bonus %" value={rules.bonus_rules.first_deposit_bonus_pct} onChange={v => updateRule('bonus_rules', 'first_deposit_bonus_pct', v)} />
                <RuleInput label="Signup Bonus (FP)" value={rules.bonus_rules.signup_bonus_xp} onChange={v => updateRule('bonus_rules', 'signup_bonus_xp', v)} />
                <RuleInput label="Referral Bonus (FP)" value={rules.bonus_rules.referral_bonus_xp} onChange={v => updateRule('bonus_rules', 'referral_bonus_xp', v)} />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Bonuses Enabled</label>
                <button onClick={() => updateRule('bonus_rules', 'enabled', !rules.bonus_rules.enabled)}
                  className={`w-12 h-6 rounded-full transition-all ${rules.bonus_rules.enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white transition-all ${rules.bonus_rules.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'referral' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Referral Rules</h3>
              <p className="text-xs text-gray-400">Player referral codes start with AR, Staff referral codes start with RA. Configure how much each party earns on a successful referral.</p>
              <div className="space-y-4">
                <div className="bg-[#0B0E14] rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-medium text-[#D4AF37]">Player Referral (AR codes)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <RuleInput label="Referrer Earns (FP)" value={rules.referral_rules.player_referrer_bonus_sc} onChange={v => updateRule('referral_rules', 'player_referrer_bonus_sc', v)} />
                    <RuleInput label="Referred Player Earns (FP)" value={rules.referral_rules.player_referred_bonus_sc} onChange={v => updateRule('referral_rules', 'player_referred_bonus_sc', v)} />
                    <RuleInput label="Referrer Earns Wallet % of Load" value={rules.referral_rules.player_referrer_load_percent} onChange={v => updateRule('referral_rules', 'player_referrer_load_percent', v)} />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400">Player Referrals Enabled</label>
                    <button onClick={() => updateRule('referral_rules', 'player_referral_enabled', !rules.referral_rules.player_referral_enabled)}
                      className={`w-10 h-5 rounded-full transition-all ${rules.referral_rules.player_referral_enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-all ${rules.referral_rules.player_referral_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="bg-[#0B0E14] rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-medium text-purple-400">Staff Referral (RA codes)</h4>
                  <p className="text-[10px] text-gray-500">Staff only sees their referred player list. Staff does not earn wallet money or FP here - referral tracking only.</p>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400">Staff Referrals Enabled</label>
                    <button onClick={() => updateRule('referral_rules', 'staff_referral_enabled', !rules.referral_rules.staff_referral_enabled)}
                      className={`w-10 h-5 rounded-full transition-all ${rules.referral_rules.staff_referral_enabled ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-all ${rules.referral_rules.staff_referral_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <h3 className="font-semibold">Support & Community Links</h3>
              <p className="text-sm text-gray-400">Configure external channels. Players see these in support menus and the landing footer when a link is set.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                    Facebook Page URL
                  </label>
                  <Input value={rules.support_channels?.facebook || ''} onChange={e => updateRule('support_channels', 'facebook', e.target.value)} placeholder="https://facebook.com/yourpage" className="bg-[#0B0E14] border-white/10" data-testid="settings-facebook" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#25D366"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z" /></svg>
                    WhatsApp Number / Channel Link
                  </label>
                  <Input value={rules.support_channels?.whatsapp || ''} onChange={e => updateRule('support_channels', 'whatsapp', e.target.value)} placeholder="+1234567890 or https://whatsapp.com/channel/..." className="bg-[#0B0E14] border-white/10" data-testid="settings-whatsapp" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0088CC"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.37-.49 1.02-.75 3.99-1.73 6.65-2.87 7.97-3.44 3.8-1.58 4.59-1.86 5.1-1.87.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06.01.24 0 .38z" /></svg>
                    Telegram Username (e.g. @support or t.me/support)
                  </label>
                  <Input value={rules.support_channels?.telegram || ''} onChange={e => updateRule('support_channels', 'telegram', e.target.value)} placeholder="@username or t.me/link" className="bg-[#0B0E14] border-white/10" data-testid="settings-telegram" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#006AFF"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2z" /></svg>
                    Messenger (Facebook page name or URL)
                  </label>
                  <Input value={rules.support_channels?.messenger || ''} onChange={e => updateRule('support_channels', 'messenger', e.target.value)} placeholder="pagename or m.me/pagename" className="bg-[#0B0E14] border-white/10" data-testid="settings-messenger" />
                </div>
              </div>
              <Button onClick={saveRules} disabled={saving} className="bg-[#D4AF37] text-black"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
          )}

          {activeTab === 'mail' && (
            <div className="glass-card rounded-xl p-5 space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Zoho Mail for This Tenant</h3>
                  <p className="text-sm text-gray-400">
                    Signup codes, password reset codes, and player emails use this tenant SMTP first.
                  </p>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full border ${mailStatus?.configured ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10'}`}>
                  {mailStatus?.configured ? (mailStatus?.inherited ? 'Using Global Mail' : 'Tenant Mail Active') : 'Needs Password'}
                </span>
              </div>

              {mailStatus?.inherited && (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-100">
                  This tenant is currently using the master/global mail setup. Save below to give this tenant its own sender.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <MailInput label="SMTP Host" value={mailConfig.smtp_host} onChange={v => setMailConfig(prev => ({ ...prev, smtp_host: v }))} placeholder="smtppro.zoho.in" />
                <MailInput label="SMTP Port" value={mailConfig.smtp_port} onChange={v => setMailConfig(prev => ({ ...prev, smtp_port: v }))} placeholder="587" />
                <MailInput label="Zoho Username" value={mailConfig.smtp_user} onChange={v => setMailConfig(prev => ({ ...prev, smtp_user: v }))} placeholder="verify@playemp.vip" />
                <MailInput label="From Email" value={mailConfig.smtp_from} onChange={v => setMailConfig(prev => ({ ...prev, smtp_from: v }))} placeholder="verify@playemp.vip" />
                <MailInput label="From Name" value={mailConfig.smtp_from_name} onChange={v => setMailConfig(prev => ({ ...prev, smtp_from_name: v }))} placeholder="Starlight" />
                <MailInput label="Zoho App Password" type="password" value={mailConfig.smtp_pass} onChange={v => setMailConfig(prev => ({ ...prev, smtp_pass: v }))} placeholder="Paste Zoho app password" />
              </div>

              <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4 space-y-3">
                <p className="text-xs text-gray-400">
                  For your new Zoho India setup use host <span className="text-[#D4AF37]">smtppro.zoho.in</span>, port <span className="text-[#D4AF37]">587</span>, username <span className="text-[#D4AF37]">verify@playemp.vip</span>.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@email.com" className="bg-[#05060A] border-white/10" />
                  <Button onClick={sendTestMail} className="bg-[#131822] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">Send Test</Button>
                </div>
              </div>

              <Button onClick={saveMailConfig} disabled={mailSaving} className="bg-[#D4AF37] text-black">
                <Save className="w-4 h-4 mr-2" /> {mailSaving ? 'Saving...' : 'Save Zoho Mail'}
              </Button>
            </div>
          )}

        </>
      )}
    </div>
  );
}

function RuleInput({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      <Input type="number" value={value || ''} onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))} placeholder="0" className="bg-[#0B0E14] border-white/10" />
    </div>
  );
}

function MailInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      <Input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0B0E14] border-white/10"
      />
    </div>
  );
}
